import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { baseOnSalesDiscountPrice, basePrice, determineFree, discountPrice, discountValue } from './pricingCalculations';
import { cx, priceFormat } from './util';
import styles from './main.module.scss';
import { translateService, Translate } from '../../services/TranslateService';
import './payment.scss';
import './PaymentCallbacks.scss';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { DataContext } from '../../contexts/dataContext';
import { GlobalStore } from '../../reduxStore';
import { ServiceRow } from './PaymentInfo';
import Modal from 'react-modal';
import { cStylesPaymentCreatedRes } from '../../CustomModalStyles';
import { useClarityStep } from '../../services/clarity/useClarityStep';
import { Provider } from '../../interfaces/PaymentForm';
import { PaymentFormFieldSet } from './PaymentFormFieldSet';
import { parseRidSession } from '../../interfaces/RidSessionData';

interface PaytrailProviderSelectionProps {
    providers: Provider[];
    back: Function;
    setRedirectHome: Function;
    reservationLength: number;
    escapeWarningOpen: boolean;
    setEscapeWarningOpen: Function;
    openSafeDriveInfo: Function;
    setShowPaymentResultScreen: Function;
    onBackToPaymentSelection?: Function;
}

export default function PaytrailProviderSelection({
    providers, back, setRedirectHome, reservationLength,
    escapeWarningOpen, setEscapeWarningOpen, openSafeDriveInfo,
    setShowPaymentResultScreen, onBackToPaymentSelection
}: PaytrailProviderSelectionProps) {

    const { services, clearSelections, vehicle, oneWayRes, pointsInEurosToBeApplied, storedCoupon,
        sendGa4Event, changeVirtualPageInfo, searchParameters, loggedIn, noVatPrices, addSafeDrivePackage
    } = useContext(DataContext);
    const dispatch = useDispatch();
    const deviceLanguage = useSelector((s: GlobalStore) => s.chauffeurReducer.deviceLanguage);
    const pricingDataType = noVatPrices ? vehicle?.pricingDataNoVat : vehicle?.pricingData;
    const couponValid = (((storedCoupon?.valid || vehicle?.vehicleData.class === 0) && pricingDataType?.coupon !== null)
        && !vehicle?.onsale && vehicle?.extraOnsales?.extra !== 1
        && !vehicle?.privateData?.private);
    const freeReservation = determineFree(vehicle, noVatPrices, services, reservationLength, couponValid, pointsInEurosToBeApplied);
    const eurosPerExtraKm = import.meta.env.VITE_EUROS_PER_EXTRA_KM ?? '';
    const useTwoFourPoints = sessionStorage.getItem('using24Points') ?? undefined;
    const resData = parseRidSession(sessionStorage.getItem('rid-1'));

    const PAYMENT_TIMEOUT_SECONDS = 900;
    const [paymentTimeRemaining, setPaymentTimeRemaining] = useState<number | null>(null);
    const [paymentExpired, setPaymentExpired] = useState(false);
    const paymentExpiredFired = useRef(false);
    const formEl = useRef<HTMLFormElement>(null);
    const [selectedProvider, setSelectedProvider] = useState<Provider | undefined>(undefined);

    useEffect(() => {
        const createTime = resData?.resCreateTime;
        if (!createTime) return;

        const calcRemaining = () => {
            const created = moment.tz(createTime, 'Europe/Helsinki');
            const now = moment.tz('Europe/Helsinki');
            const elapsed = now.diff(created, 'seconds');
            return Math.max(PAYMENT_TIMEOUT_SECONDS - elapsed, 0);
        };

        const remaining = calcRemaining();
        if (remaining <= 0) {
            setPaymentExpired(true);
            setPaymentTimeRemaining(0);
            return;
        }

        setPaymentTimeRemaining(remaining);

        const interval = setInterval(() => {
            const secs = calcRemaining();
            setPaymentTimeRemaining(secs);
            if (secs <= 0) {
                clearInterval(interval);
                setPaymentExpired(true);
                if (!paymentExpiredFired.current) {
                    paymentExpiredFired.current = true;
                    sendGa4Event("payment_timeout");
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [resData?.resCreateTime]);

    Modal.setAppElement('body');

    useClarityStep('paymentProvider', {
        city: searchParameters?.location?.formatted_address || searchParameters?.pickupCity || '',
        start: searchParameters?.startDate?.toString() || '',
        end: searchParameters?.endDate?.toString() || '',
        loggedIn: loggedIn ?? false,
        paymentMethod: services.paymentMethod,
        oneway: oneWayRes,
        safeDrive: addSafeDrivePackage ?? false
    });

    useEffect(() => {
        const scrollPoint = document.getElementById("selectPaymentProviderScrollPoint");
        if (scrollPoint) {
            scrollPoint.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        changeVirtualPageInfo('paymentProvider');
    }, []);

    const handleEscapePendingPayment = () => {
        sendGa4Event('CustomerDiscardedReservation');
        clearSelections();
        dispatch({ type: "CLEAR_SELECTIONS", payload: null });
        setRedirectHome(true);
    };

    const redirectToSameSearch = () => {
        if (!resData) {
            setRedirectHome(true);
            return;
        }
        const baseUrl = `${window.location.origin}/`;
        const startDate = resData.startDate ? resData.startDate.replace(/@ /g, '') : '';
        const endDate = resData.endDate ? resData.endDate.replace(/@ /g, '') : '';
        const params = new URLSearchParams();

        sessionStorage.removeItem('paymentSuccess');
        sessionStorage.removeItem('rid-1');
        sessionStorage.removeItem('paymentPending');
        sessionStorage.removeItem('paytrailProviders');
        sessionStorage.removeItem('paymentProvider');
        sessionStorage.removeItem('reservationCreated');
        sessionStorage.removeItem('selectedCar');
        sessionStorage.removeItem('carIdChanged');
        sessionStorage.removeItem('carIdReservedCheck');
        sessionStorage.setItem('route', '/');
        sessionStorage.setItem('page', '0');
        if (resData.lang && resData.lang !== 'fi') params.append('lang', 'en');
        const datesInFuture = startDate && moment.tz(startDate, 'DD.MM.YYYY HH:mm', 'Europe/Helsinki').isAfter(moment.tz('Europe/Helsinki'));
        if (datesInFuture) {
            params.append('startdate', startDate);
            if (endDate) params.append('enddate', endDate);
        }
        if (resData.returnLoc) params.append('oneway', '1');
        if (resData.locationSearch) params.append('city', String(resData.locationSearch));

        window.location.href = `${baseUrl}?${params.toString()}`;
    };

    useEffect(() => {
        if (selectedProvider) {
            sessionStorage.setItem('paymentPending', JSON.stringify(true));
            sendGa4Event("paytrail_provider_selected", { provider_name: selectedProvider.name });
            formEl.current?.submit();
        }
    }, [selectedProvider]);

    if (!vehicle || !pricingDataType) {
        return (
            <div className={cx(styles.r24FullWidth)}>
                <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                    <span>{translateService.translate('paymentCallbackScreens.vehicleInfoNotFound')}</span>
                </div>
            </div>
        );
    }

    return <>
        <div className={cx(styles.r24FullWidth)}>
            <Modal isOpen={escapeWarningOpen} style={cStylesPaymentCreatedRes}>
                <div style={{ display: 'flex', flexDirection: 'column', textAlign: "center", justifyContent: "center" }}>
                    <span>
                        {translateService.translate('paymentCallbackScreens.exitPaymentWarning')}
                    </span>
                    <button onClick={() => handleEscapePendingPayment()}
                        className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
                        {translateService.translate('paymentCallbackScreens.yesReturnHome')}
                    </button>
                    <button onClick={() => { sendGa4Event("navigate_back", { back_to: "back_from_payment" }); setEscapeWarningOpen(false) }}
                        className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
                        {translateService.translate('vehicleSearch.close')}
                    </button>
                </div>
            </Modal>
            <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                <table className={styles.r24PriceTable} style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th><Translate content={'vehicleSearch1.services'} /></th>
                            <th><Translate content={'vehicleSearch1.price'} /></th>
                            <th>{(couponValid || vehicle.onsale ||
                                (pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0)
                                || (vehicle?.privateData?.vip)) ?
                                <Translate content={'vehicleSearch1.discountedPrice'} /> : null}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td> {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} </td>
                            <td> {priceFormat(pricingDataType?.base.totalPrice / 100)} € </td>
                            <td><strong>
                                {vehicle.onsale ?
                                    (priceFormat((vehicle.onsale.price * vehicle.onsale.discountmultiplier) / 100) + ' €')
                                    :
                                    (couponValid && priceFormat((pricingDataType?.withCoupon?.totalPrice ?? 1) / 100) + ' €')
                                }</strong>
                            </td>
                        </tr>
                        {oneWayRes ?
                            <>
                                {services.kmRange === 100 &&
                                    <>
                                        <td> <Translate content={'vehicleSearch1.onewayKmIncludedService'} />
                                            {100 * reservationLength} km.&nbsp;
                                        </td>
                                        <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                                        {couponValid &&
                                            <td>
                                                <strong><Translate content={'vehicleSearch1.includedInPrice'} /></strong>,&nbsp;
                                                <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                                            </td>
                                        }
                                        {!couponValid && <td></td>}
                                    </>
                                }
                                {(services.kmRange === 0) &&
                                    <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                                {services.kmRange === 0 && <>
                                    <td>{priceFormat(pricingDataType?.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                    </td>
                                    {couponValid && <td>
                                        {priceFormat(pricingDataType?.withCoupon!.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                    </td>}
                                </>}
                            </>
                            : <>
                                <tr>
                                    {(services.kmRange === 100 || services.kmRange === 400) &&
                                        <td>{services.kmRange * reservationLength} km</td>}
                                    {(services.kmRange === 0 || services.kmRange === 1000) &&
                                        <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                                    {services.kmRange === 100 &&
                                        <>
                                            <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                                            {couponValid &&
                                                <td>
                                                    <strong><Translate content={'vehicleSearch1.includedInPrice'} /></strong>,&nbsp;
                                                    <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                                                </td>
                                            }
                                        </>}
                                    {services.kmRange === 400 && <>
                                        <td>{priceFormat(pricingDataType?.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€,&nbsp;
                                            <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                                        </td>
                                        {couponValid &&
                                            <td>
                                                <strong>
                                                    {priceFormat(pricingDataType?.base.pricePerKilometer / 100 *
                                                        Math.max(services.kmRange - 100, 0) * reservationLength)} &nbsp;€
                                                </strong>,&nbsp; <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                                            </td>}
                                    </>}
                                    {services.kmRange === 1000 && <>
                                        <td>{priceFormat(pricingDataType?.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€
                                        </td>
                                        {couponValid && <td>
                                            <strong>
                                                {priceFormat(pricingDataType?.base.pricePerKilometer / 100 *
                                                    Math.max(services.kmRange - 100, 0) * reservationLength)} &nbsp;€
                                            </strong>
                                        </td>}
                                    </>}
                                    {services.kmRange === 0 && <>
                                        <td>{priceFormat(pricingDataType?.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                        </td>
                                        {couponValid && <td>
                                            {priceFormat(pricingDataType?.withCoupon!.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                                        </td>}
                                    </>}
                                    {!couponValid && <td></td>}
                                </tr>
                            </>
                        }

                        {services.safeDrivePackage ?
                            <ServiceRow included={services.safeDrivePackage} back={back} scrollLocation={''}
                                label={<Translate content={'vehicleSearch1.safeDrivePackage'} />}
                                content={<>{priceFormat(pricingDataType?.base.safeDrivePackage / 100 * services.safeDriveReservationLength)} €</>}
                                discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.safeDrivePackage / 100 * services.safeDriveReservationLength)} €</strong>}
                                safeDriveInfo={openSafeDriveInfo} /> : null}

                        <ServiceRow included={services.insurance.includes('1030')} back={back} scrollLocation={'insurancesAndDeductibles'}
                            label={<>
                                <Translate content={'vehicleSearch1.fairdeductible'} />&nbsp;
                                {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export ? 4 : 1))} &nbsp;€
                            </>}
                            content={<><Translate content={'vehicleSearch1.includedInPrice'} /></>}
                        />

                        <ServiceRow included={services.insurance.includes('1053')} back={back} scrollLocation={'insurancesAndDeductibles'}
                            label={<><Translate content={'vehicleSearch.services.midDeductionText'} />&nbsp;
                                {priceFormat(vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export ? 4 : 1))} €</>}
                            content={<>{priceFormat(pricingDataType?.base.midDeductible / 100 * services.insuranceReservationLength)} €</>}
                            discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.midDeductible / 100 * services.insuranceReservationLength)} €</strong>}
                        />

                        <ServiceRow included={services.insurance.includes('1028')} back={back} scrollLocation={'insurancesAndDeductibles'}
                            label={<>
                                <Translate content={'vehicleSearch1.deductible'} />&nbsp;
                                {priceFormat(vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export ? 4 : 1))} €</>}
                            content={<>{priceFormat(pricingDataType?.base.lowDeductible / 100 * services.insuranceReservationLength)} €</>}
                            discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.lowDeductible / 100 * services.insuranceReservationLength)} €</strong>}
                        />

                        <ServiceRow included={services.windshield} back={back} scrollLocation={'additionalInsurances'}
                            label={<Translate content={'vehicleSearch1.windshieldInsurance'} />}
                            content={<>{priceFormat(pricingDataType?.base.windshieldInsurance / 100 * services.windshieldReservationLength)} €</>}
                            discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.windshieldInsurance / 100 * services.windshieldReservationLength)} €</strong>}
                        />

                        <ServiceRow included={services.tireInsurance} back={back} scrollLocation={'additionalInsurances'}
                            label={<Translate content={'vehicleSearch1.tireInsurance'} />}
                            content={<>{priceFormat(pricingDataType?.base.tireInsurance / 100 * services.tireInsuranceReservationLength)} €</>}
                            discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.tireInsurance / 100 * services.tireInsuranceReservationLength)} €</strong>}
                        />

                        {!oneWayRes ?
                            <ServiceRow included={services.cancellationInsurance} back={back} scrollLocation={'additionalInsurances'}
                                label={<Translate content={'vehicleSearch1.cancellationInsurance'} />}
                                content={<>{priceFormat(pricingDataType?.base.cancellationInsurance / 100 * services.cancellationInsuranceReservationLength)} €</>}
                                discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.cancellationInsurance / 100 * services.cancellationInsuranceReservationLength)} €</strong>}
                            /> : null}

                        <ServiceRow included={services.additionalDriver} back={back} scrollLocation={'additionalDriver'}
                            label={<Translate content={'vehicleSearch1.additionalDriver'} />}
                            content={<>{priceFormat(pricingDataType?.base.additionalDriver / 100 * services.additionalDriverReservationLength)} €</>}
                            discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.additionalDriver / 100 * services.additionalDriverReservationLength)} €</strong>}
                        />

                        {(vehicle.vehicleData.hasHitch && services.hook) &&
                            <ServiceRow included={services.hook} back={back} scrollLocation={'additionalServices'}
                                label={<Translate content={'vehicleSearch1.hook'} />}
                                content={<>{priceFormat(pricingDataType?.base.towHitch / 100 * services.hookReservationLength)} €</>}
                                discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.towHitch / 100 * services.hookReservationLength)} €</strong>} />
                        }

                        {services.hasHandtruck &&
                            <ServiceRow included={services.handTruck} back={back} scrollLocation={'additionalServices'}
                                label={<Translate content={'vehicleSearch1.handTruck'} />}
                                content={<Translate content={'vehicleSearch1.includedInPrice'} />}
                                discount={couponValid && <strong><Translate content={'vehicleSearch1.includedInPrice'} /></strong>} />
                        }

                        {(vehicle.vehicleData.exportPermits.balticStates ||
                            vehicle.vehicleData.exportPermits.scandinavia || vehicle.vehicleData.exportPermits.europe) ?
                            <ServiceRow included={services.export} back={back} scrollLocation={'export'}
                                label={<Translate content={'vehicleSearch1.export'} />}
                                content={<>{priceFormat(pricingDataType?.base.export / 100 * services.exportReservationLegth)} €</>}
                                discount={couponValid && <strong>{priceFormat(pricingDataType?.withCoupon!.export / 100 * services.exportReservationLegth)} €</strong>} />
                            : null
                        }

                        {services.co2compensation === '' &&
                            <tr>
                                <td><Translate content={'vehicleSearch1.co2Compensation'} /></td>
                                <td className={styles.notIncludedText}><Translate content={'vehicleSearch1.notIncluded'} /></td>
                                <td></td>
                            </tr>
                        }
                        {services.co2compensation === '1047' &&
                            <tr>
                                <td><Translate content={'vehicleSearch1.miniCompensation'} /></td>
                                <td>{priceFormat(pricingDataType?.base.hundredTwentyFiveGramsCOTwo! / 100 * services.co2CompensationReservationLength)} €</td>
                                {couponValid &&
                                    <td>
                                        <strong>{priceFormat(pricingDataType?.withCoupon!.hundredTwentyFiveGramsCOTwo! / 100 * services.co2CompensationReservationLength)} €</strong>
                                    </td>}
                            </tr>
                        }
                        {services.co2compensation === '1048' &&
                            <tr>
                                <td><Translate content={'vehicleSearch1.basicCompensation'} /></td>
                                <td>
                                    {priceFormat(pricingDataType?.base.fiveHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                                </td>
                                {couponValid &&
                                    <td>
                                        <strong>{priceFormat(pricingDataType?.withCoupon!.fiveHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)} €</strong>
                                    </td>}
                            </tr>
                        }
                        {services.co2compensation === '1049' &&
                            <tr>
                                <td>
                                    <Translate content={'vehicleSearch1.overCompensation'} />
                                </td>
                                <td>
                                    {priceFormat(pricingDataType?.base.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                                </td>
                                {couponValid &&
                                    <td>
                                        {priceFormat(pricingDataType?.withCoupon!.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                                    </td>
                                }
                            </tr>
                        }
                        {couponValid &&
                            <tr>
                                <td> <Translate content={'vehicleSearch1.discount'} /> {vehicle.vehicleData.class === 0 ? ' (SmartSaver)' : null}</td>
                                <td> 0 €</td>
                                <td>
                                    <strong> -{priceFormat(discountValue(vehicle, noVatPrices, services, reservationLength))} €</strong>
                                </td>
                            </tr>
                        }
                        {useTwoFourPoints && pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
                            <tr>
                                <td><strong>{translateService.translate('payment.twoFourPoints')}</strong></td>
                                <td> 0 €</td>
                                <td><strong>-{priceFormat(pointsInEurosToBeApplied ?? 0)} € </strong></td>
                            </tr>
                            : null}
                        <tr>
                            <td><strong>
                                <Translate with={{
                                    type: noVatPrices
                                        ? translateService.translate('vehicleSearch.vatPercent0') :
                                        translateService.translate('vehicleSearch.vatPercent25')
                                }}
                                    content='vehicleSearch1.totalVat' />
                            </strong></td>
                            <td>
                                {priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength))} €
                            </td>
                            <td>
                                {
                                    vehicle.onsale ?
                                        <strong>{priceFormat(baseOnSalesDiscountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                                        : (couponValid ?
                                            <strong>{priceFormat(discountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                                            :
                                            pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
                                                <strong>{priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} € </strong>
                                                : null
                                        )
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {paymentExpired ? (
                <div className='callback-info-container pb-3' id='paymentExpiredScrollPoint'>
                    <div className='payment-callback-section py-3 mobile-top-line'>
                        <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                            <div className={cx(styles['payment-footer-lastminutenotice-expired'], styles.center)}>
                                <span className={styles.lastminutecounternotice1}>
                                    {translateService.translate('paymentCallbackScreens.paymentExpiredHeading')}
                                </span>
                                <div className={styles.lastminutecounternotice2}>
                                    {translateService.translate('paymentCallbackScreens.paymentExpiredText')}
                                </div>
                            </div>
                        </div>
                        <div className='d-flex justify-content-center mb-2 mt-2'>
                            <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                onClick={() => redirectToSameSearch()}>
                                {translateService.translate('paymentCallbackScreens.resCancelledBtn1')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {paymentTimeRemaining !== null && (
                        <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                            <div className={cx(
                                paymentTimeRemaining <= 60
                                    ? styles['payment-footer-lastminutenotice-urgent']
                                    : styles['payment-footer-lastminutenotice'],
                                styles.center
                            )}>
                                <span className={styles.lastminutecounternotice1}>
                                    <strong className={styles.countdownTimeLine}>{paymentTimeRemaining % 2 === 0 ? '⏳' : '⌛'} {`${String(Math.floor(paymentTimeRemaining / 60)).padStart(2, '0')}:${String(paymentTimeRemaining % 60).padStart(2, '0')}`}</strong> &nbsp;{translateService.translate('paymentCallbackScreens.paymentCountdownRemaining')}
                                    <br />
                                    {paymentTimeRemaining <= 60
                                        ? translateService.translate('paymentCallbackScreens.paymentCountdownUrgent')
                                        : translateService.translate('paymentCallbackScreens.paymentCountdownText')}
                                </span>
                                <div className={styles.lastminutecounternotice2}>
                                    {translateService.translate('paymentCallbackScreens.paymentCountdownNotice')}
                                </div>
                            </div>
                        </div>
                    )}
                    <span id={"selectPaymentProviderScrollPoint"}>{translateService.translate('paymentCallbackScreens.selectPaymentProvider')}</span>
                    <div className="payment-list">
                        {providers.map((provider) => (
                            <div className="payment-method p-2" key={`payment-method-${provider.name}-${provider.url}`}>
                                <button className="payment-button btn btn-all btn-light shadow"
                                    onClick={() => setSelectedProvider(provider)}>
                                    <img alt={provider.name}
                                         src={provider.svg}
                                         className="bankLogo"
                                         width="150"
                                         height="60"
                                         loading="lazy"
                                    />
                                </button>
                            </div>
                        ))}
                        {/* Provider URLs are BE-sourced (Paytrail API) and point to individual bank domains
                           (Nordea, Danske, OP, etc.) — too many to allowlist in validateRedirectUrl. */}
                        <form action={selectedProvider?.url ?? ''} method="POST" ref={formEl} hidden={true}>
                            <PaymentFormFieldSet
                                name={selectedProvider?.name ?? ''}
                                svg={selectedProvider?.svg ?? ''}
                                parameters={selectedProvider?.parameters ?? []}
                                url={selectedProvider?.url ?? ''}
                            />
                        </form>
                    </div>

                    <div className={cx('d-flex min-width-320', styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)}>
                        <div className={cx('flex-grow-1', styles.r24ChildFlex1, styles.r24RightMargin5)}>
                            <button type="button" className={cx("btn btn-all", styles.greenBackButton)} onClick={() => {
                                sendGa4Event("navigate_back", { back_to: "back_from_payment_provider" });
                                if (onBackToPaymentSelection) {
                                    onBackToPaymentSelection();
                                } else {
                                    setEscapeWarningOpen(true);
                                }
                            }}>
                                <ArrowBackIcon className={styles.arrowIcon} />
                            </button>
                        </div>
                        <div className={cx('flex-grow-1', styles.r24ChildFlex4, styles.r24LeftMargin5)} />
                    </div>
                </>
            )}
        </div>
    </>;
}
